import React, { ReactElement } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { Divider, ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';

import {
  BeamsIcon,
  BearingEnvelopesIcon,
  CeilingsIcon,
  ChasesandStepsIcon,
  ConnectionViewIcon,
  FeaturePatternContainersIcon,
  RoofsIcon,
  TrussEnvelopesIcon,
  WorkLinesIcon,
} from '@paragon/material';

import { isLayoutBasedOnRoofPlanes, isLayoutViewActive } from '../../../store/selectors/layoutModeSelectors';
import {
  getActiveBeamMode,
  getActiveCeilingMode,
  getActiveConnectionMode,
  getActiveEnvelopeMode,
  getActiveFeatureAreaMode,
  getActiveFeaturePatternContainerMode,
  getActiveNonStructuralWallMode,
  getActiveRoofMode,
  getActiveWallMode,
  getActiveWorkLineMode,
  getConfiguredBeamMode,
  getConfiguredCeilingMode,
  getConfiguredConnectionMode,
  getConfiguredEnvelopeMode,
  getConfiguredFeatureAreaMode,
  getConfiguredFeaturePatternContainerMode,
  getConfiguredNonStructuralBearingMode,
  getConfiguredRoofMode,
  getConfiguredWallMode,
  getConfiguredWorkLineMode,
} from '../../../store/selectors/layoutVisualOptionsSelectors';
import {
  RenderModeType,
  SetManualRenderModePayload,
  setManualRenderModeValue,
  setRenderModeControlledItemVisible,
  setRenderModeToAutomatic,
  setRenderModeToManual,
  UnknownRenderModeString,
} from '../../../store/slices/visualOptionsSlice';
import { isTouchEvent } from '../../../three/util/events/touch';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { RenderModeToolbarButton, useRenderModeToolbarMenuContext } from './RenderModeToolbarButton';

export function LayoutRenderModeButtons() {
  const layoutViewActive = useAppSelector(isLayoutViewActive);
  const inRoofPlaneLayout = useAppSelector(isLayoutBasedOnRoofPlanes);

  const configuredContainerMode = useAppSelector(getConfiguredFeaturePatternContainerMode);
  const actualContainerMode = useAppSelector(getActiveFeaturePatternContainerMode);

  const configuredFeatureAreaMode = useAppSelector(getConfiguredFeatureAreaMode);
  const actualFeatureAreaMode = useAppSelector(getActiveFeatureAreaMode);

  const configuredRoofMode = useAppSelector(getConfiguredRoofMode);
  const actualRoofMode = useAppSelector(getActiveRoofMode);

  const configuredCeilingMode = useAppSelector(getConfiguredCeilingMode);
  const actualCeilingMode = useAppSelector(getActiveCeilingMode);

  const configuredWallMode = useAppSelector(getConfiguredWallMode);
  const actualWallMode = useAppSelector(getActiveWallMode);

  const configuredNonStructuralBearingMode = useAppSelector(getConfiguredNonStructuralBearingMode);
  const actualNonStructuralWallMode = useAppSelector(getActiveNonStructuralWallMode);

  const configuredBeamMode = useAppSelector(getConfiguredBeamMode);
  const actualBeamMode = useAppSelector(getActiveBeamMode);

  const configuredEnvelopeMode = useAppSelector(getConfiguredEnvelopeMode);
  const actualEnvelopeMode = useAppSelector(getActiveEnvelopeMode);

  const configuredConnectionMode = useAppSelector(getConfiguredConnectionMode);
  const actualConnectionMode = useAppSelector(getActiveConnectionMode);

  const configuredWorkLineMode = useAppSelector(getConfiguredWorkLineMode);
  const actualWorkLineMode = useAppSelector(getActiveWorkLineMode);

  if (!layoutViewActive) return null;

  return (
    <>
      <StandardRenderModeToolbarButton
        icon={<FeaturePatternContainersIcon />}
        primary={{
          type: 'featurePatternContainerMode',
          title: 'Feature Pattern Containers',
          configured: configuredContainerMode,
          actual: actualContainerMode,
          withAutomaticMode: true,
          options: {
            translucent: 'Visible',
            wireframe: 'Wireframe',
            solid: 'Solid',
          },
        }}
      />

      <StandardRenderModeToolbarButton
        icon={<ChasesandStepsIcon />}
        disabled={actualContainerMode === 'none'}
        primary={{
          type: 'featureAreaMode',
          title: 'Chases & Steps',
          configured: configuredFeatureAreaMode,
          actual: actualFeatureAreaMode,
          options: {
            translucent: 'Visible',
            wireframe: 'Wireframe',
            solid: 'Solid',
          },
        }}
      />

      <StandardRenderModeToolbarButton
        icon={<RoofsIcon />}
        primary={{
          type: 'roofMode',
          title: 'Roofs',
          configured: configuredRoofMode,
          actual: actualRoofMode,
          withAutomaticMode: true,
          options: {
            translucent: 'Planes',
            wireframe: 'Wireframe',
            solid: 'Solid',
          },
        }}
      />

      {inRoofPlaneLayout && (
        <StandardRenderModeToolbarButton
          icon={<CeilingsIcon />}
          primary={{
            type: 'ceilingMode',
            title: 'Ceilings',
            configured: configuredCeilingMode,
            actual: actualCeilingMode,
            withAutomaticMode: true,
            options: {
              translucent: 'Planes',
              wireframe: 'Wireframe',
              solid: 'Solid',
            },
          }}
        />
      )}

      <StandardRenderModeToolbarButton
        icon={<BearingEnvelopesIcon />}
        primary={{
          type: 'wallMode',
          title: 'Walls',
          configured: configuredWallMode,
          actual: actualWallMode,
          options: {
            'hatched-wireframe': 'Hatched',
            wireframe: 'Wireframe',
            solid: 'Solid',
          },
        }}
        secondary={{
          type: 'nonStructuralBearingMode',
          title: 'Non-Structural Walls',
          configured: configuredNonStructuralBearingMode,
          actual: actualNonStructuralWallMode,
          options: {
            'hatched-wireframe': 'Hatched',
            wireframe: 'Wireframe',
            solid: 'Solid',
          },
        }}
      />

      <StandardRenderModeToolbarButton
        icon={<BeamsIcon />}
        primary={{
          type: 'beamMode',
          title: 'Beams',
          configured: configuredBeamMode,
          actual: actualBeamMode,
          options: {
            'hatched-wireframe': 'Hatched',
            wireframe: 'Wireframe',
            solid: 'Solid',
          },
        }}
      />

      <StandardRenderModeToolbarButton
        icon={<TrussEnvelopesIcon />}
        primary={{
          type: 'envelopeMode',
          title: 'Truss Envelopes',
          configured: configuredEnvelopeMode,
          actual: actualEnvelopeMode,
          options: {
            components: 'Designs',
            trussSolids: 'Solids',
            envelope: 'Envelopes',
          },
        }}
      />

      <StandardRenderModeToolbarButton
        icon={<ConnectionViewIcon />}
        primary={{
          type: 'connectionMode',
          title: 'Connections',
          configured: configuredConnectionMode,
          actual: actualConnectionMode,
          options: {
            hardware: 'Hardware',
            connections: {
              label: 'Connections',
              tooltip: 'Truss-to-truss connections and user-specified carried chords',
            },
            hardwareAndConnections: 'Both',
          },
        }}
      />

      <StandardRenderModeToolbarButton
        icon={<WorkLinesIcon />}
        primary={{
          type: 'workLineMode',
          title: 'Work Lines',
          configured: configuredWorkLineMode,
          actual: actualWorkLineMode,
          options: {
            visible: 'Visible',
          },
        }}
      />
    </>
  );
}

type MenuOption = string | { label: string; tooltip?: string };
function getOptionDetails(optionValue: MenuOption): { label: string; tooltip?: string } {
  return typeof optionValue === 'string' ? { label: optionValue } : optionValue;
}

interface RenderModeConfig {
  type: RenderModeType;
  title: string;
  configured: string;
  actual: string;
  options: Partial<Record<UnknownRenderModeString, MenuOption>>;
  withAutomaticMode?: boolean;
}
interface RenderModeMenuSectionProps {
  config: RenderModeConfig;
  closeMenuAfterItemSelectionIfNeeded: (event: React.MouseEvent) => void;
}

function RenderModeMenuSection({
  config: { type, title, configured, actual, options, withAutomaticMode },
  closeMenuAfterItemSelectionIfNeeded,
}: RenderModeMenuSectionProps) {
  const dispatch = useAppDispatch();

  return (
    <>
      <MenuItem disabled>{title}</MenuItem>

      {withAutomaticMode === true && (
        <MenuItem
          selected={configured === 'auto'}
          onClick={(event) => {
            dispatch(configured === 'auto' ? setRenderModeToManual(type) : setRenderModeToAutomatic(type));

            closeMenuAfterItemSelectionIfNeeded(event);
          }}
        >
          {configured === 'auto' && (
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
          )}
          <ListItemText inset={configured !== 'auto'}>Automatic</ListItemText>
        </MenuItem>
      )}

      <Divider component="li" sx={{ marginY: '4px!important' }} />

      {Object.entries(options).map(([mode, optionValue]) => {
        const { label, tooltip } = getOptionDetails(optionValue);

        let menuItem = (
          <MenuItem
            key={mode}
            selected={(withAutomaticMode === true ? configured : actual) === mode}
            onClick={(event) => {
              if (configured === mode) {
                dispatch(setRenderModeToAutomatic(type));
              } else {
                dispatch(setManualRenderModeValue({ type, mode } as SetManualRenderModePayload));
              }

              closeMenuAfterItemSelectionIfNeeded(event);
            }}
          >
            {actual === mode && (
              <ListItemIcon>
                <CheckIcon />
              </ListItemIcon>
            )}
            <ListItemText inset={actual !== mode}>{label}</ListItemText>
          </MenuItem>
        );

        if (tooltip) {
          menuItem = (
            <Tooltip key={mode} title={tooltip} placement="right">
              {menuItem}
            </Tooltip>
          );
        }

        return menuItem;
      })}

      <MenuItem
        selected={configured === 'none'}
        onClick={(event) => {
          dispatch(setRenderModeControlledItemVisible(type, configured === 'none'));
          closeMenuAfterItemSelectionIfNeeded(event);
        }}
      >
        {configured === 'none' && (
          <ListItemIcon>
            <CheckIcon />
          </ListItemIcon>
        )}
        <ListItemText inset={configured !== 'none'}>Hidden</ListItemText>
      </MenuItem>
    </>
  );
}

function StandardRenderModeToolbarButton(props: {
  icon: ReactElement;
  disabled?: boolean;

  primary: RenderModeConfig;
  secondary?: RenderModeConfig;
}) {
  const dispatch = useAppDispatch();
  const menuContext = useRenderModeToolbarMenuContext();

  function cycleOptions(options?: { directionSign?: number; wrap?: boolean }) {
    const optionKeys = [...Object.keys(props.primary.options), 'none'];
    const currentIndex = optionKeys.indexOf(
      props.primary.configured === 'auto' ? props.primary.actual : props.primary.configured,
    );

    let nextIndex = currentIndex + (options?.directionSign ?? 1);

    if (options?.wrap) {
      nextIndex = (nextIndex + optionKeys.length) % optionKeys.length;
    } else {
      nextIndex = Math.max(0, Math.min(optionKeys.length - 1, nextIndex));
    }

    if (optionKeys[nextIndex] === 'none') {
      dispatch(setRenderModeControlledItemVisible(props.primary.type, false));
    } else {
      dispatch(
        setManualRenderModeValue({
          type: props.primary.type,
          mode: optionKeys[nextIndex],
        } as SetManualRenderModePayload),
      );
    }
  }

  function closeMenuAfterItemSelectionIfNeeded(event: React.MouseEvent) {
    if (isTouchEvent(event.nativeEvent)) {
      menuContext.closeActiveMenu();
    }
  }

  return (
    <RenderModeToolbarButton
      menuKey={props.primary.type}
      icon={props.icon}
      title={props.primary.title}
      disabled={props.disabled}
      onMouseClick={() => cycleOptions({ wrap: true })}
      onMouseUp={(event) => {
        if (event.button === 1) {
          dispatch(setRenderModeControlledItemVisible(props.primary.type, props.primary.configured === 'none'));
        }
      }}
      onWheel={(event) => {
        cycleOptions({ directionSign: Math.sign(event.deltaY) });
      }}
    >
      <RenderModeMenuSection
        config={props.primary}
        closeMenuAfterItemSelectionIfNeeded={closeMenuAfterItemSelectionIfNeeded}
      />

      {props.secondary && (
        <>
          <Divider component="li" sx={{ marginY: '4px!important' }} />
          <RenderModeMenuSection
            config={props.secondary}
            closeMenuAfterItemSelectionIfNeeded={closeMenuAfterItemSelectionIfNeeded}
          />
        </>
      )}
    </RenderModeToolbarButton>
  );
}
